import { Popover } from 'antd';
import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import html2canvas from 'html2canvas';

// api
import {
  getSequenceActivitesCard,
  getSequenceOverviewPerformenceMetrics,
  updateSequenceStatus,
} from '../api/SquenceOverview';
import { getSequence, updateSequence } from '../api/sequence';

// assets
import Arrow from '../../../assets/customSVG/Arrow';
import FullArrow from '../../../assets/customSVG/FullArrow';
import Download from '../../../assets/customSVG/campaigns/Download';
import EditIcon from '../../../assets/customSVG/EditIcon';
import Flow from '../../../assets/customSVG/sequence/Flow';
import { ICONS } from '../../../assets/icons';

// constants
import {
  PERFORMENCE_DATE_FILTERS_LIST,
  PERFORMENCE_TABS_FILTER,
} from '../constants/sequenceOverview';

// hooks
import useToggle from '../../../hooks/useToggle';
import useOutsideClick from '../../../hooks/useOutsideClick';

// helpers
import { dateConverter } from '../../../helper/dateConverter';

// constants
import {
  MAX_SEQUENCE_LENGTH,
  SEQUENCE_STATUS_STYLES,
} from '../constants/sequence';

// redux
import { useAspDispatch, useAspSelector } from '../../../test/jest-redux-hooks';
import { updateToggleToast } from '../../../reduxToolkit/appSlice';

// components
import Breadcrumbs from '../../../components/commonComponents/breadcrumbs';
import Switch from '../../../components/commonComponents/step/switch';
import Cards from '../components/sequence/overview/Cards';
import ContactList from '../components/sequence/overview/ContactList';
import Preview from '../components/sequence/overview/Preview';
import Tooltip from '../../../components/commonComponents/tooltip';
import Performance from '../components/overviewChart/Performance';
import Loader from '../../../components/commonComponents/Loader/Index';
import FilterCalendar from '../../../components/commonComponents/filterHome/FilterCalendar';

// notification
import NotificationContext from '../../../providers/NotificationProvider';
import { showErrorToast, showSuccessToast } from '../../../components/commonComponents/gxAntdToast/toastUtils';

const cardsArray = [
  {
    id: 1,
    icon: ICONS?.ConversionCost,
    label: 'Re-engagement Rate',
    description:
      'Number / Percentage of customers re-engaging after a prior conversation.',
    key: 're_engagement',
    value: '0',
  },
  {
    id: 2,
    icon: ICONS?.ActiveConversion,
    label: 'Active Conversations',
    key: 'active_conversations',
    value: '0',
  },
  {
    id: 3,
    icon: ICONS?.FirstReply,
    label: 'URL Clicks Count',
    description:
      'Number of unique URL clicks across all templates in the sequence.',
    key: 'clicked_count',
    value: '0',
  },
  {
    id: 4,
    icon: ICONS?.ConvertionWaitTime,
    label: 'Avg. Wait Time for Conversion',
    key: 'avg_wait_time',
    value: '0',
  },
];

const SequenceMetrix = () => {
  const { sequence_id, campaign_id: paramCampaignId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const campaign_id = paramCampaignId || location.state?.campaign_id;
  const isStandalone = !paramCampaignId;
  const { currentBrand, toggleToast } = useAspSelector((state) => state.app);
  const dispatch = useAspDispatch();

  const [isLoading, setIsLoading] = useToggle(false);
  const [isEditing, setIsEditing] = useState(true);
  const [sequenceDetails, setSequenceDetails] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [flowDetails, setFlowDetails] = useState(null);
  const [cardsMetrics, setCardsMetrics] = useState(cardsArray);
  const [isEditName, setIsEditName] = useToggle(false);
  const [editedName, setEditedName] = useState('');
  // Date filter states
  const [selectedDateFilter, setSelectedDateFilter] = useState(PERFORMENCE_DATE_FILTERS_LIST[0]);
  const datePickerRef = useRef(null);
  
  const [dateFilterState, setDateFilterState] = useState({
    isOpen: false,
    isCustomPickerOpen: false,
    previousFilter: PERFORMENCE_DATE_FILTERS_LIST[0],
    selectedFilter: PERFORMENCE_DATE_FILTERS_LIST[0],
    filteredDates: {
      startDate: moment().subtract(6, 'days'),
      endDate: moment()
    },
    customDateRange: ''
  });

  // Handle click outside to close date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        if (dateFilterState.isCustomPickerOpen) {
          // Only handle the click outside if we're not in the process of selecting dates
          const isClickOnCalendar = event.target.closest('.react-calendar');
          if (!isClickOnCalendar) {
            handleCustomDateRange(null);
          }
        } else if (dateFilterState.isOpen) {
          setDateFilterState(prev => ({
            ...prev,
            isOpen: false
          }));
        }
      }
    };

    // Add event listener when component mounts
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up event listener when component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dateFilterState.isOpen, dateFilterState.isCustomPickerOpen]);

  const [filter, setFilter] = useState([]);
  const [performenceData, setPerformenceData] = useState({});
  const [filteredData, setFilteredData] = useState({});
  const [updatedPerformanceData, setUpdatedPerformanceData] = useState(
    PERFORMENCE_TABS_FILTER
  );
  const captureRef = useRef();
  // notification
  const notification = useContext(NotificationContext);

  const breadcumbsData = [
    { label: 'Campaigns', path: '/user/campaign/all-campaigns' },
    {
      label: 'Campaign Execution',
      path: `/user/campaign/view/${campaign_id}/execution`,
    },
    { label: 'Sequence Overview', path: '' },
  ];

  const handleFilterChange = async (filter) => {
    if (filter.key === 'custom') {
      setDateFilterState(prev => ({
        ...prev,
        isOpen: false,
        isCustomPickerOpen: true,
        previousFilter: selectedDateFilter,
        // Clear any previous custom date range when opening the picker
        customDateRange: ''
      }));
      return;
    }
    
    // Get dates based on the selected filter and campaign dates
    const dates = getDates(
      filter.key, 
      sequenceDetails?.start_date, 
      sequenceDetails?.end_date
    );
    
    // Update the state with the new filter and dates
    setDateFilterState(prev => ({
      ...prev,
      isOpen: false,
      isCustomPickerOpen: false,
      selectedFilter: filter,
      customDateRange: '', // Clear custom date range when selecting a predefined filter
      filteredDates: {
        startDate: moment(dates.startDate, 'YYYY-MM-DD'),
        endDate: moment(dates.endDate, 'YYYY-MM-DD')
      }
    }));
    
    // Update the selected filter in the parent state
    setSelectedDateFilter(filter);
    
    // Fetch metrics for the new date range
    await getPerformenceMetrics(dates);
  };

  const handleCustomDateRange = async (dateRange) => {
    if (!dateRange) {
      // Close the picker and revert to previous filter when clicking outside without selecting dates
      setDateFilterState(prev => ({
        ...prev,
        isCustomPickerOpen: false
      }));

      if (dateFilterState.previousFilter) {
        await handleFilterChange(dateFilterState.previousFilter);
      }
      return;
    }

    // Update the date range in the state
    const [start, end] = dateRange.split(' - ');
    
    // Parse the dates from the format 'MMM D, YYYY' (e.g., 'Jul 10, 2025')
    const newDates = {
      startDate: moment(start, 'MMM D, YYYY').format('YYYY-MM-DD'),
      endDate: moment(end, 'MMM D, YYYY').format('YYYY-MM-DD')
    };
    
    const customFilter = { 
      ...PERFORMENCE_DATE_FILTERS_LIST[3], 
      label: dateRange 
    };

    setDateFilterState(prev => ({
      ...prev,
      isCustomPickerOpen: false,
      selectedFilter: customFilter,
      filteredDates: {
        startDate: moment(newDates.startDate),
        endDate: moment(newDates.endDate)
      },
      customDateRange: dateRange
    }));
    
    setSelectedDateFilter(customFilter);
    await getPerformenceMetrics(newDates);
  };

  const getDates = (key, campaignStart, campaignEnd) => {
    // Parse dates with proper timezone handling
    const today = moment().utc().endOf('day');
    // Subtract 1 day from campaign start date to allow selection from the actual start date
    const campaignStartDate = campaignStart ? moment.utc(campaignStart).subtract(1, 'day').startOf('day') : null;
    const campaignEndDate = campaignEnd ? moment.utc(campaignEnd).endOf('day') : null;

    let startDate, endDate;
    let daysToSubtract = 6; // Default to 7 days (6 days + 1)
    
    // Determine how many days to subtract based on the filter
    switch (key) {
      case 'last7days':
        daysToSubtract = 6; // 7 days total (inclusive)
        break;
      case 'last14days':
        daysToSubtract = 13; // 14 days total (inclusive)
        break;
      case 'last28days':
        daysToSubtract = 27; // 28 days total (inclusive)
        break;
      default:
        daysToSubtract = 6; // Default to 7 days
    }
    
    // Rule 1: If today is before or on campaign end date
    if (campaignEndDate && today.isSameOrBefore(campaignEndDate)) {
      endDate = today.clone();
      startDate = today.clone().subtract(daysToSubtract, 'days').startOf('day');
      
      // If start date is before campaign start, adjust to campaign start
      if (campaignStartDate && startDate.isBefore(campaignStartDate)) {
        startDate = campaignStartDate.clone();
      }
    } 
    // Rule 2: If today is after campaign end date
    else if (campaignEndDate) {
      endDate = campaignEndDate.clone();
      startDate = campaignEndDate.clone().subtract(daysToSubtract, 'days').startOf('day');
      
      // If start date is before campaign start, adjust to campaign start
      if (campaignStartDate && startDate.isBefore(campaignStartDate)) {
        startDate = campaignStartDate.clone();
      }
    }
    // Fallback: No campaign dates available
    else {
      endDate = today.clone();
      startDate = today.clone().subtract(daysToSubtract, 'days').startOf('day');
    }
    
    // Final safety check to ensure valid date range
    if (startDate.isAfter(endDate)) {
      startDate = endDate.clone();
    }
    
    const result = {
      startDate: startDate.utc().format('YYYY-MM-DD'),
      endDate: endDate.utc().format('YYYY-MM-DD')
    };
    
    return result;
  };
  
  // Function to get initial date range based on campaign dates
  const getInitialDateRange = (campaignStart, campaignEnd) => {
    const today = moment();
    const campaignStartDate = campaignStart ? moment(campaignStart) : null;
    const campaignEndDate = campaignEnd ? moment(campaignEnd) : null;
    
    // If campaign has ended, use campaign end date as reference
    const endDate = campaignEndDate && campaignEndDate.isBefore(today) 
      ? campaignEndDate.clone() 
      : today.clone();
      
    // Calculate start date (7 days before end date)
    let startDate = endDate.clone().subtract(6, 'days');
    
    // Ensure start date is not before campaign start
    if (campaignStartDate && startDate.isBefore(campaignStartDate)) {
      startDate = campaignStartDate.clone();
    }
    
    return {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    };
  };

  const handleSubmit = async () => {
    if (!editedName?.trim()) {
      showErrorToast(notification, 'The sequence name cannot be left empty.');
      return;
    }

    try {
      const payload = {
        campaign_id,
        brand_id: currentBrand?.brand_id,
        name: editedName,
      };
      const res = await updateSequence(sequence_id, payload);

      if (res?.status === 200) {
        setSequenceDetails((prev) => ({ ...prev, name: editedName }));
        setIsEditing(false);
        setIsEditName(false);
      } else {
        showErrorToast(notification, res?.response?.data?.message);
      }
    } catch (err) {
      console.warn(err);
    }
  };

  const handleCancel = () => {
    setEditedName(sequenceDetails?.name);
    setIsEditing(false);
    setIsEditName(false);
  };

  const inputRef = useOutsideClick(() => handleCancel());

  useEffect(() => {
    sequenseData();
    getActivitesCardData();
    
    // We'll set initial dates after sequenceDetails are loaded
  }, []);

  useEffect(() => {
    if (sequenceDetails?.start_date && sequenceDetails?.end_date) {
      // Get initial date range based on campaign dates
      const initialDates = getInitialDateRange(
        sequenceDetails.start_date, 
        sequenceDetails.end_date
      );
      
      setDateFilterState(prev => ({
        ...prev,
        filteredDates: {
          startDate: moment(initialDates.startDate),
          endDate: moment(initialDates.endDate)
        }
      }));
      
      // Fetch metrics with the initial date range
      getPerformenceMetrics(initialDates);
    }
  }, [sequenceDetails]);

  useEffect(() => {
    setUpdatedPerformanceData(
      PERFORMENCE_TABS_FILTER?.map((item) => {
        switch (item.label) {
          case 'Triggered':
            return { ...item, count: performenceData?.TRIGGERED || 0 };
          case 'Failure':
            return { ...item, count: performenceData?.FAILED || 0 };
          case 'Completed':
            return { ...item, count: performenceData?.COMPLETED || 0 };
          case 'Abandoned':
            return { ...item, count: performenceData?.ABANDONED || 0 };
          default:
            return item;
        }
      })
    );
  }, [performenceData]);

  const handlePerformance = (value) => {
    setFilter((prevFilter) => {
      if (prevFilter.includes(value)) return [];
      else return [value];
    });
  };

  const convertObjectToArray = (obj) => {
    return Object.entries(obj).map(([key, value]) => {
      return { key, value };
    });
  };

  const convertArrayToObject = (arr) => {
    return arr.reduce((acc, { key, value }) => {
      acc[key] = value;
      return acc;
    }, {});
  };

  const filterDatas = (data, key) => {
    let updatedData = data;

    if (key) {
      const arrayData = convertObjectToArray(data);
      const res = arrayData?.filter((d) => d?.key === key);
      const convert = convertArrayToObject(res);

      updatedData = {
        TRIGGERED: data?.TRIGGERED || 0,
        FAILURE: data?.FAILED || 0,
        COMPLETED: data?.COMPLETED || 0,
        ABANDONED: data?.ABANDONED || 0,
        ...convert,
      };
    }
    setFilteredData(updatedData);
  };

  const sequenseData = async () => {
    setIsLoading(true);
    const payload = {
      brandId: currentBrand?.brand_id,
      campaignId: campaign_id,
      flow: 'false',
    };
    try {
      const res = await getSequence(sequence_id, payload);
      if (res?.status === 200) {
        setSequenceDetails(res?.data?.data?.sequence);
        setCampaignDetails(res?.data?.data?.campaign);
        setFlowDetails({
          nodes: res?.data?.data?.nodes,
          edges: res?.data?.data?.edges,
        });
        setEditedName(sequenceDetails?.name);
      } else {
        dispatch(
          updateToggleToast([
            ...toggleToast,
            {
              id: toggleToast?.length + 1,
              content: res?.response?.data?.message,
              status: 'Error',
              duration: '',
            },
          ])
        );
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setIsLoading(false);
    }
  };

  const sequenseStatusUpdate = useCallback(async () => {
    let status = ['Draft', 'New']?.includes(getStatusStyles()?.text)
      ? 'activate'
      : ['Running', 'ACTIVE', 'SCHEDULED']?.includes(getStatusStyles()?.text)
      ? 'pause'
      : ['Paused']?.includes(getStatusStyles()?.text)
      ? 'start'
      : 'activate';
    const payload = {
      brand_id: currentBrand?.brand_id,
      campaign_id: campaign_id,
      state_action: status,
    };
    try {
      const res = await updateSequenceStatus(sequence_id, payload);
      if (res?.status === 200)
        setSequenceDetails((prev) => ({
          ...prev,
          status: res?.data?.sequence_status,
        }));
      else
        dispatch(
          updateToggleToast([
            ...toggleToast,
            {
              id: toggleToast?.length + 1,
              content: res?.response?.data?.message,
              status: 'Error',
              duration: '',
            },
          ])
        );
    } catch (error) {
      console.warn(error);
    }
  }, [sequenceDetails]);

  const getActivitesCardData = async () => {
    try {
      const payload = {
        brandId: currentBrand?.brand_id,
        campaignId: campaign_id,
      };
      const res = await getSequenceActivitesCard(sequence_id, payload);
      if (res?.status === 200) {
        const data = cardsArray?.map((card) => {
          return { ...card, value: res?.data[card?.key] };
        });
        setCardsMetrics(data);
      } else {
        dispatch(
          updateToggleToast([
            ...toggleToast,
            {
              id: toggleToast?.length + 1,
              content: res?.response?.data?.message,
              status: 'Error',
              duration: '',
            },
          ])
        );
      }
    } catch (error) {
      console.warn(error);
    }
  };

  const getPerformenceMetrics = async (dates) => {
    const payload = {
      brandId: currentBrand?.brand_id,
      campaignId: campaign_id,
      startData: dates?.startDate,
      endDate: dates?.endDate,
    };
    const res = await getSequenceOverviewPerformenceMetrics(
      sequence_id,
      payload
    );
    if (res?.status === 200) {
      setPerformenceData(res?.data?.data);
      filterDatas(res?.data?.data, filter?.[0] || null);
    }
  };

  const handleEdit = () => {
    setIsEditName(true);
    setIsEditing(true);
    setEditedName(sequenceDetails?.name);
  };

  const onChange = (e) => {
    const newValue = e?.target?.value?.replace(/[^A-Za-z0-9-_ ]/g, '');
    setEditedName(newValue);
  };

  const takeScreenshot = async () => {
    try {
      const element = captureRef.current;

      element.style.position = 'relative';
      element.style.zIndex = '9999';
      element.style.padding = '0px';
      element.style.backgroundColor = '#f9f9f9';

      const canvas = await html2canvas(element, {
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
        backgroundColor: '#f9f9f9',
      });

      // Convert canvas to image and trigger download
      const imgData = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imgData;
      link.download = 'screenshot.png';
      link.click();
    } catch (error) {
      console.error('Error taking screenshot:', error);
    }
  };

  const getStatusStyles = useCallback(
    () => SEQUENCE_STATUS_STYLES[sequenceDetails?.status],
    [sequenceDetails]
  );

  const allowToEditStatus = useCallback(() => {
    const isActive = flowDetails?.nodes?.filter((n) => n?.status === 'ACTIVE');

    return (
      flowDetails?.nodes?.length >= 5 &&
      isActive?.length >= 5 &&
      flowDetails?.edges?.length > 4
    );
  }, [flowDetails]);

  const performencePopupContent = (
    <div className='w-full min-w-36 h-full flex flex-col px-1.5'>
      {PERFORMENCE_DATE_FILTERS_LIST?.map((list) => {
        return (
          <div
            key={list.key}
            className='w-full h-12 py-1.5 flex flex-1 items-center justify-center border-b border-[var(--border-100)] cursor-pointer last:border-none'
            onClick={async () => {
              setSelectedDateFilter(list);
              if (list?.key !== 'custom') {
                const dates = getDates(
                  list?.key,
                  sequenceDetails?.start_date,
                  sequenceDetails?.end_date
                );
                await getPerformenceMetrics(dates);
                setIsOpenDatesFilter(false);
                
                // Update the date filter state
                setDateFilterState(prev => ({
                  ...prev,
                  selectedFilter: list,
                  filteredDates: {
                    startDate: moment(dates.startDate),
                    endDate: moment(dates.endDate)
                  }
                }));
              } else {
                setIsOpenCustomDateFilter(true);
              }
            }}>
            <div className='w-full h-9 flex items-center justify-center flex-1 text-sm weight-medium hover:bg-[var(--BG-50)] rounded-md'>
              {list?.label}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (isLoading)
    return (
      <div className='flex-row align-center justify-center'>
        <Loader
          Width={20}
          Height={20}
          // loaderBg='white'
        />
      </div>
    );

  return (
    <div
      className='w-full h-screen flex flex-col gap-7 px-7 py-8 max-[1600px]:px-5 max-[1600px]:py-6 max-[1600px]:gap-4'
      ref={captureRef}>
      {/* <Breadcrumbs breadcrumbs={breadcumbsData} /> */}
      <div className='w-full bg-white p-4 rounded-2xl flex items-center gap-4'>
        <div className='w-1/2 flex flex-1 flex-col justify-center gap-2.5'>
          {/* <div className='flex items-center gap-2'>
            <p
              className={`size-4 rounded contents-['']`}
              style={{ background: campaignDetails?.display_color }}
            />
            <h5 className='w-fit max-w-52 truncate capitalize text-base weight-medium text-[var(--font-600)] max-[1440px]:text-sm'>
              {campaignDetails?.name}
            </h5>
            <Tooltip
              content='Campaign Name'
              position='right'>
              <img
                src={ICONS?.conversationAction}
                alt='info'
                className='size-5'
              />
            </Tooltip>
          </div> */}
          <div className='w-auto h-9 flex items-center gap-2.5'>
            <div
              className='size-8 flex items-center justify-center rotate-180 p-1.5 border-2 border-[#2D3036] rounded-full cursor-pointer'
              onClick={() =>
                isStandalone
                  ? navigate('/user/sequences')
                  : navigate(`/user/campaign/view/${campaign_id}/execution`)
              }
            >
              <FullArrow color='#2D3036' />
            </div>
            <Switch
              isChecked={
                ['Running', 'Scheduled', 'Active']?.includes(
                  getStatusStyles()?.text
                )
                  ? true
                  : false
              }
              allow={allowToEditStatus()}
              toggleSwitch={(event) => {
                event.stopPropagation();
                event.preventDefault();
                if (allowToEditStatus()) sequenseStatusUpdate();
              }}
            />
            {isEditName ? (
              <div
                ref={inputRef}
                className="px-3 py-1.5 flex items-center gap-2 border border-[var(--border-100)] rounded-md w-full max-w-sm"
              >
                <input
                  type="text"
                  value={editedName}
                  maxLength={MAX_SEQUENCE_LENGTH}
                  onChange={onChange}
                  className="text-sm font-semibold text-[#2D3036] outline-none flex-grow"
                />
          
                {/* Character counter */}
                <p className="text-xs text-[#616874] font-medium whitespace-nowrap">
                  {editedName?.length}/{MAX_SEQUENCE_LENGTH}
                </p>
          
                {/* Action icons */}
                {isEditing && (
                  <div className="flex gap-2 items-center">
                    <CheckOutlined
                      onClick={handleSubmit}
                      className="text-green-600 hover:text-green-700 cursor-pointer text-base"
                    />
                    <CloseOutlined
                      onClick={handleCancel}
                      className="text-red-500 hover:text-red-600 cursor-pointer text-base"
                    />
                  </div>
                )}
              </div>
            ) : (
              <Tooltip content={sequenceDetails?.name}>
                <h4 className='w-fit max-w-52 truncate capitalize text-xl weight-bold text-[#2D3036] max-[1440px]:text-base'>
                  {sequenceDetails?.name}
                </h4>
              </Tooltip>
            )}
            {!isEditName && (
              <Tooltip
                content={'Edit'}
                onClick={handleEdit}>
                <EditIcon
                  width={24}
                  height={24}
                  className={'hover:bg-[var(--BG-50)] p-1 rounded-md'}
                />
              </Tooltip>
            )}
          </div>
          <div className='w-auto h-[30px] flex items-center'>
            <div
              className='w-fit h-full flex items-center gap-1.5 px-2 mr-2.5 rounded-md border-r border-gray-200'
              style={{
                background: getStatusStyles()?.bg,
              }}>
              {getStatusStyles()?.icon && (
                <img
                  src={getStatusStyles()?.icon}
                  alt={getStatusStyles()?.text}
                />
              )}
              <p className='text-sm weight-semibold text-white max-[1440px]:text-xs'>
                {getStatusStyles()?.text}
              </p>
            </div>
            <p className='text-sm weight-medium text-[var(--font-600)] ml-2.5 max-[1440px]:text-xs'>
              Duration{' '}
              {dateConverter(sequenceDetails?.start_date)?.formatedShortDate} -{' '}
              {dateConverter(sequenceDetails?.end_date)?.formatedShortDate}
            </p>
          </div>
        </div>
        <div className='w-1/2 flex flex-1 items-center justify-end gap-4'>
          <Link
            to={'flow'}
            className='flex items-center gap-1.5 px-3 py-2 border border-[#E8E8EA] rounded-md hover:bg-[#E8E8EA]'>
            <Flow color='#2D3036' />
            <p className='text-sm weight-medium max-[1440px]:text-xs'>
              Go to Flow
            </p>
          </Link>
          <Tooltip
            content={'Download Reports'}
            onClick={takeScreenshot}
            className={'-ml-[45px] mt-0.5'}>
            <Download className='border border-[#E8E8EA] w-11 h-[37px] py-2 px-0.5 rounded-md hover:bg-[#E8E8EA] cursor-pointer' />
          </Tooltip>
        </div>
      </div>

      <div className='w-full h-fit max-h-[calc(100vh-20vh)] flex-1'>
        <div className='w-full h-full flex gap-6 relative max-[1600px]:gap-4'>
          <div className='w-full !max-w-[76%] h-full flex flex-col flex-1 gap-6 rounded-2xl overflow-y-scroll max-[1600px]:gap-4'>
            {/* <div className='w-full h-fit flex gap-6 max-[1600px]:!flex-col max-[1600px]:gap-4'> */}
              {/* Sequence Cards */}
              <div className='w-full grid grid-cols-2 gap-6 max-[1600px]:gap-4 max-[1600px]:w-full max-[1600px]:grid-cols-4'>
                {cardsMetrics?.map((card) => {
                  return <Cards data={card} />;
                })}
              </div>

              {/* Performance Chart */}
              <div className='w-full bg-white p-4 rounded-2xl'>
                <div className='w-full h-12 flex justify-between text-[var(--contentText)] border-b border-[#E8E8EA]'>
                  <h5 className='text-lg weight-semibold py-1'>Performance</h5>
                  <div className='flex items-center'>
                    <div className='relative' ref={datePickerRef}>
                      {/* Main Date Filter Button */}
                      <div 
                        className='flex items-center border border-[var(--border-input)] rounded-md px-3 py-2 text-sm weight-medium cursor-pointer'
                        onClick={(e) => {
                          e.stopPropagation();
                          setDateFilterState(prev => ({
                            ...prev,
                            isOpen: !prev.isOpen,
                            isCustomPickerOpen: false
                          }));
                        }}
                      >
                        <p>{selectedDateFilter?.label || 'Select Date Range'}</p>
                        <img 
                          src={ICONS?.chevronDownCarousel} 
                          className='ml-2 w-3 h-3' 
                          alt='dropdown'
                        />
                      </div>

                      {/* Date Filter Dropdown */}
                      {dateFilterState.isOpen && (
                        <div className='absolute top-full right-0 mt-1 z-50 bg-white rounded-lg shadow-lg min-w-36'>
                          <div className='p-2'>
                            {PERFORMENCE_DATE_FILTERS_LIST?.map((item) => (
                              <div 
                                key={item.key} 
                                className='p-2 hover:bg-gray-100 rounded cursor-pointer'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFilterChange(item);
                                }}
                              >
                                {item.label}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Custom Date Picker */}
                      {dateFilterState.isCustomPickerOpen && (
                        <div className='absolute top-full right-0 mt-1 z-50 bg-white p-4 rounded-lg shadow-lg w-[350px]' onClick={e => e.stopPropagation()}>
                          <FilterCalendar
                            setCustomDateVisible={(visible) => setDateFilterState(prev => ({
                              ...prev,
                              isCustomPickerOpen: visible
                            }))}
                            customStartDate={sequenceDetails?.start_date ? moment(sequenceDetails.start_date).subtract(1, 'day').format('YYYY-MM-DD') : moment().subtract(30, 'days').format('YYYY-MM-DD')}
                            customEndDate={sequenceDetails?.end_date || moment().format('YYYY-MM-DD')}
                            setCustomDateRange={handleCustomDateRange}
                            setStartDate={(date) => {
                              setDateFilterState(prev => ({
                                ...prev,
                                filteredDates: {
                                  ...prev.filteredDates,
                                  startDate: moment(date, 'YYYY-MM-DD')
                                }
                              }));
                            }}
                            setEndDate={(date) => {
                              setDateFilterState(prev => ({
                                ...prev,
                                filteredDates: {
                                  ...prev.filteredDates,
                                  endDate: moment(date, 'YYYY-MM-DD')
                                }
                              }));
                            }}
                            customDateRange={dateFilterState.customDateRange}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className=''>
                  <Performance
                    performanceList={filteredData}
                    startDate={dateFilterState.filteredDates.startDate?.format('YYYY-MM-DD')}
                    endDate={dateFilterState.filteredDates.endDate?.format('YYYY-MM-DD')}
                    updatedPerformanceData={updatedPerformanceData}
                    isBroadcast={false}
                    rootClassName='mr-0 !h-[38vh]'
                  />
                  <div className='flex-row align-center text-[var(--textBlack)] mt-10 pt-5'>
                    {updatedPerformanceData?.map((item) => (
                      <div
                        key={item?.id}
                        className={`w-full flex-col items-center gap-[1.3vw] border-r-[1px] last:border-r-0 cursor-pointer`}
                        onClick={() => {
                          handlePerformance(item?.key);
                          filterDatas(performenceData, item?.key);
                        }}>
                        <div
                          className={`rounded-lg w-95 m-5 p-5 ${
                            filter?.includes(item?.key) &&
                            item?.label !== 'Conversions'
                              ? 'bg-[#E8E8EA]'
                              : ''
                          }`}>
                          <div className='flex-row justify-center items-center'>
                            <img
                              src={item?.icon}
                              alt='summary'
                              className='w-5'
                            />
                            <span className='no-search-body px-2'>
                              {item?.label}
                            </span>
                            <Tooltip content={`Overall ${item?.label} Count`}>
                              <img
                                src={ICONS?.conversationAction}
                                alt='performanceIcon'
                                className=''
                              />
                            </Tooltip>
                          </div>
                          <div className='flex-row justify-center pt-5'>
                            <span
                              className={` ${
                                item?.count === undefined ||
                                item?.label === 'Conversions'
                                  ? 'count-style-light'
                                  : 'count-style'
                              }`}>
                              {item?.count || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            {/* </div> */}

            {/* Contact List */}
            <ContactList sequenceDetails={sequenceDetails} />
          </div>

          <div className='w-full !max-w-[24%] h-full max-h-[calc(100vh-28vh)] flex-1 overflow-hidden'>
            <Preview />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SequenceMetrix;
